import React, { useEffect, useState } from 'react';
import marked from 'marked';
import { SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.min.css';

import { DynamicContentItem } from '../../interfaces/dynamicContent.types';
import { DynamicContentLayout } from '../../enums/dynamicContentLayout.enum';
import { ButtonData } from '../../interfaces/buttonData.types';
import { PageType } from '../../enums/pageType.enum';

import {
  TextContent,
  Photo,
  GalleryContainer,
  SliderPhoto,
  FullscreenPhotoContainer,
  FullscreenPhoto,
  CloseButton,
  CloseIcon,
  ActionButton,
  Video,
  AddToComparisonButton,
  ButtonContainer,
  LocalSectionContainer,
} from './dynamicContentComponent.styled';
import { DynamicContentComponentData } from './dynamicContentComponent.types';
import { Table } from './components/table/table.component';
import { Counter } from './components/counter/counter.component';
import { NumericList } from './components/numericList/numericList.component';
import { renderExternalLink, renderInternalLink, renderWhenTrue, renderWhenTrueOr } from '../../utils';
import { Slider } from './components/slider/slider.component';
import CloseIconFile from './images/close.svg';

export const DynamicContentComponent: React.FC<DynamicContentComponentData> = (data: DynamicContentComponentData) => {
  const { header, dynamicContent, currentLanguage, pageId, renderCaseStudyProduct } = data;
  const [fullscreenPhoto, setFullscreenPhoto] = useState(null);
  const [localComparisonData, setLocalComparisonData] = useState(JSON.parse(localStorage.getItem('comparisonData')) || []);
  const isProductInComparisonOnInit = () => {
    const comparisonData = JSON.parse(localStorage.getItem('comparisonData')) || [];
    return comparisonData.includes(pageId);
  };
  const [isProductInComparison, setIsProductInComparison] = useState(isProductInComparisonOnInit());


  const renderer: marked.Renderer = {
    link(href: string, title: string, text: string): string {
      const isExternalUrl = href.includes('http');
      return `
        <a href="${href}" ${isExternalUrl ? 'target="_blank"' : ''}>
          ${text}
        </a>
      `;
    },
    listitem(text: string): string {
      return `
        <li>
          <span class='bullet'></span>
          <span class='content'>${text}</span>
        </li>
      `;
    }
  } as unknown as marked.Renderer;

  marked.use({ renderer });
  marked.setOptions({
    headerIds: false,
    breaks: true,
  });

  const renderPhotos = (dynamicContentItem: DynamicContentItem) => dynamicContentItem.photoGallery.map((photoUrl: string) =>
    <SwiperSlide key={photoUrl}>
      <SliderPhoto src={photoUrl} alt='' onClick={() => setFullscreenPhoto(photoUrl)} />
    </SwiperSlide>
  );

  const renderButton = (buttonData: ButtonData) => {
    const renderLink = renderWhenTrueOr(
      () => renderInternalLink(buttonData.label, buttonData.internalPage, currentLanguage),
      () => renderExternalLink(buttonData.label, buttonData.externalPage)
    );

    return renderLink(buttonData.positionType === PageType.Internal);
  };

  const sendEvent = () => {
    const event = document.createEvent('HTMLEvents');
    event.initEvent('storage:update', true, true);
    document.dispatchEvent(event);
  }

  document.addEventListener('storage:update', (event) => {
    const comparisonData = JSON.parse(localStorage.getItem('comparisonData')) || [];
    setLocalComparisonData(comparisonData);
  });

  const toogleProductInComparison = () => {
    const comparisonData = JSON.parse(localStorage.getItem('comparisonData')) || [];
    setLocalComparisonData(comparisonData);
    if (comparisonData.length < 3 && !isProductInComparison) {
      comparisonData.push(pageId);
      localStorage.setItem('comparisonData', JSON.stringify(comparisonData));
      localStorage.setItem('comparisonDataLangKey', currentLanguage);
      setIsProductInComparison(true);
      sendEvent();
    } else if (isProductInComparison) {
      comparisonData.splice(comparisonData.indexOf(pageId), 1);
      localStorage.setItem('comparisonData', JSON.stringify(comparisonData));
      localStorage.setItem('comparisonDataLangKey', currentLanguage);
      setIsProductInComparison(false);
      sendEvent();
    }
  };

  useEffect(() => {
    const comparisonData = JSON.parse(localStorage.getItem('comparisonData')) || [];
    setLocalComparisonData(comparisonData);
  }, [localStorage]);

  const renderDynamicContent = () => dynamicContent.map((dynamicContentItem: DynamicContentItem) => {
    switch (dynamicContentItem.acf_fc_layout) {
      case DynamicContentLayout.TextContent:
        return (
          <TextContent
            key={dynamicContentItem.text}
            className='text-content'
            dangerouslySetInnerHTML={{__html: marked.parse(dynamicContentItem.text)}}
          />
        );
      case DynamicContentLayout.Photo:
        return (
          <Photo key={dynamicContentItem.photo?.localFile?.url} src={dynamicContentItem.photo?.localFile?.url} />
        );
      case DynamicContentLayout.Video:
        return (
          <Video controls key={dynamicContentItem.videoFile?.localFile?.url}>
            <source src={dynamicContentItem.videoFile?.localFile?.url} />
          </Video>
        );
      case DynamicContentLayout.Counter:
        return (
          <Counter
            counterData={dynamicContentItem.counterSection}
            key={dynamicContentItem.counterSection.counterRow[0].value}
          />
        );
      case DynamicContentLayout.NumericList:
        return (
          <NumericList
            rows={dynamicContentItem.numericList.numericRowData}
            key={dynamicContentItem.numericList.numericRowData[0].value}
          />
        );
      case DynamicContentLayout.TableWithButton:
        const { comparisonButtonSettings } = dynamicContentItem.tableWithButton;
        const { removeButtonLabel, addButtonLabel } = comparisonButtonSettings || {};
        const comparisonButtonLabel = isProductInComparison ? removeButtonLabel : addButtonLabel;
        const renderActionButton = renderWhenTrue(() => (
          <ButtonContainer>
            <AddToComparisonButton
              disabled={!isProductInComparison && localComparisonData.length === 3}
              onClick={toogleProductInComparison}
            >
              {comparisonButtonLabel}
            </AddToComparisonButton>
            <ActionButton>
              {renderButton(dynamicContentItem.tableWithButton.buttonData)}
            </ActionButton>
          </ButtonContainer>
        ));
        return (
          <>
            <Table
              rowData={dynamicContentItem.tableWithButton.tableData.rowData}
              key={dynamicContentItem.tableWithButton.tableData.rowData[0].value}
            />
            {renderActionButton(dynamicContentItem.tableWithButton.buttonData.active)}
          </>
        );
      case DynamicContentLayout.PhotoGallery:
        return (
          <GalleryContainer
            key={dynamicContentItem.photoGallery[0]}
            className='gallery-container'
          >
            <Slider>
              {renderPhotos(dynamicContentItem)}
            </Slider>
          </GalleryContainer>
        );
    }
  });

  if (!dynamicContent) {
    return <div />;
  }
  
  return (
    <LocalSectionContainer
      header={header.bigHeader.mainContent}
      subheader={header.bigHeader.descriptionContent}
      smallHeader={header.smallImageHeader.text}
      smallHeaderImage={header.smallImageHeader.photo}
      headerType={header.headerType}
      renderCaseStudyProduct={renderCaseStudyProduct}
    >
      {renderDynamicContent()}
      <FullscreenPhotoContainer isVisible={!!fullscreenPhoto}>
        <CloseButton onClick={() => setFullscreenPhoto(null)}>
          <CloseIcon src={CloseIconFile} alt='' />
        </CloseButton>
        <FullscreenPhoto src={fullscreenPhoto} alt='' />
      </FullscreenPhotoContainer>
    </LocalSectionContainer>
  );
}
